import React, { useState } from 'react';
import { isQaEnvironment } from '../utils/subdomain';
import './Sidebar.css';

const Sidebar = ({
  isOpen,
  onClose,
  currentPage,
  onNavigate,
  user,
  subdomain
}) => {
  // Menu para administrador
  const adminMenuItems = [
    {
      id: 'home-admin',
      title: 'Home',
      icon: '🏠',
      path: '/'
    },
    {
      id: 'users',
      title: 'Usuários',
      icon: '👥',
      path: '/users'
    },
    {
      id: 'support',
      title: 'Chat Suporte',
      icon: '💬',
      path: '/support'
    },
    {
      id: 'support-generic',
      title: 'Suporte Genérico',
      icon: '🗨️',
      path: '/support-generic'
    },
    // ===== PROGRAMAS E FASES: o único item com submenu =====
    // Agrupado a pedido do Caique (12/08) e posicionado LOGO APOS Suporte Generico (tambem a
    // pedido): e o item de trabalho diario do CS e estava perdido no meio da lista, abaixo de
    // telas de uso esporadico. Os três filhos são as superfícies da feature, e as
    // ABAS de Configurações continuam DENTRO da página, não como netos aqui: elas compartilham o
    // ScopeSelector, e trocar de aba por rota perderia o escopo escolhido a cada clique. Duas
    // hierarquias empilhadas também fariam o CS clicar duas vezes para o mesmo lugar.
    ...(user?.role === 'admin' ? [{
      id: 'programas-fases',
      title: 'Programas e Fases',
      icon: '\u{1F9ED}',
      children: [
        { id: 'programas', title: 'Programas', icon: '\u{1F5C2}\uFE0F', path: '/programas' },
        { id: 'turmas', title: 'Turmas', icon: '\u{1F465}', path: '/turmas' },
        { id: 'grupos-de-turmas', title: 'Grupos de turmas', icon: '\u{1F333}', path: '/grupos-de-turmas' },
        { id: 'configuracoes', title: 'Configurações', icon: '\u2699\uFE0F', path: '/config/prompts' },
        // Escalas: só interno (@alia.do), como o `requireInterno` das rotas /schedule/* do BFF; conta de
        // cliente com role='admin' não vê o item (e a rota recusaria de qualquer jeito).
        ...(String(user?.username || '').toLowerCase().endsWith('@alia.do')
          ? [{ id: 'escalas', title: 'Escalas', icon: '\u{1F4C5}', path: '/escalas/padroes' }] : []),
      ]
    }] : []),
    // 'Agente Dashboard' escondido do menu (a pedido) — rota/feature mantidas.
    //
    // Dashboard Analítico NÃO entra aqui. Ele é o painel do CLIENTE, servido no domínio
    // dele (`leroymerlin.alia.do`, `raviera.alia.do`, …) e montado a partir do Host — no
    // portal admin ele não tem empresa para mostrar sem alguém passar `?company=`.
    // A entrada existia e foi retirada a pedido (01/09/2026).
    //
    // A ROTA continua de pé, e é de propósito: quem publica precisa revisar o rascunho
    // antes de promover, e faz isso abrindo /analytic-dashboard/<slug>?company=<empresa>
    // pela URL. O `escopoDashboard` do BFF só honra o `?company=` no portal admin e com
    // role=admin, então tirar o item do menu não tira o preview de ninguém — só deixa de
    // oferecer, no menu do admin, uma tela que ali abriria vazia.
    // No menu de CLIENTE (defaultMenuItems, mais abaixo) ele é o PRIMEIRO item desde
    // 02/09/2026, quando o Dash Operacional saiu de lá.

    // A "Revisão de Notas e Pontos" (/revisao-pontos): o alerta de dislike da Home leva às Reclamações
    // desde 05/10/2026; esta fica no menu para revisar atendimento sem reclamação.
    ...(user?.role === 'admin' ? [{
      id: 'revisao-pontos',
      title: 'Revisão de Notas e Pontos',
      icon: '⭐',
      path: '/revisao-pontos'
    }] : []),
    // Reclamações (VAR + dislike no feedback) — fila e revisão em etapas, somente role = admin
    ...(user?.role === 'admin' ? [{
      id: 'reclamacoes-pontos',
      title: 'Reclamações (VAR e dislikes)',
      icon: '🙋',
      path: '/reclamacoes-pontos'
    }] : []),
    // Promoções de whitelabel — propostas do MCP para levar whitelabel de QA/HMG a este
    // ambiente. Só interno (@alia.do), como o `requireInterno` das rotas; quem DECIDE ainda
    // precisa do tema do MCP, conferido pela API.
    ...(user?.role === 'admin' && String(user?.username || '').toLowerCase().endsWith('@alia.do') ? [{
      id: 'promocoes-whitelabel',
      title: 'Promoções de whitelabel',
      icon: '\u{1F680}',
      path: '/promocoes-whitelabel'
    }] : []),
    // Feedbacks / Atendimentos (busca por filtros) — somente para role = admin
    ...(user?.role === 'admin' ? [{
      id: 'feedbacks-search',
      title: 'Feedbacks / Atendimentos',
      icon: '💭',
      path: '/feedbacks-search'
    }] : []),
    // Termo de aceite do app (versões, política por escopo e relatório) — somente para role = admin
    ...(user?.role === 'admin' ? [{
      id: 'termos',
      title: 'Termo de aceite',
      icon: '📝',
      path: '/termos'
    }] : []),
    // Palavras-chave (Keyterms da ElevenLabs) — somente para role = admin
    ...(user?.role === 'admin' ? [{
      id: 'keyterms-config',
      title: 'Palavras-chave (Keyterms)',
      icon: '🔤',
      path: '/keyterms-config'
    }] : []),

    // Endpoints Publicados (Dash Builder) — somente para role = admin
    // Sincronizador de Ambientes — role admin + admin irrestrito da Aliado Admin
    // (can_sync do /auth/me) + somente QA. Esconder o item é cosmético: a API recusa.
    ...(user?.role === 'admin' && user?.can_sync && isQaEnvironment() ? [{
      id: 'sync-ambientes',
      title: 'Sincronizar Ambientes',
      icon: '🔀',
      path: '/sync'
    }] : []),
    // Endpoints Publicados
    ...(user?.role === 'admin' ? [{
      id: 'endpoints-publicados',
      title: 'Endpoints Publicados',
      icon: '🔌',
      path: '/endpoints-publicados'
    }] : []),
    // Versões do app (pushtokens por cliente) — somente para role = admin
    ...(user?.role === 'admin' ? [{
      id: 'app-versions',
      title: 'Versões do app',
      icon: '📱',
      path: '/app-versions'
    }] : []),
    // Apps — onde baixar o app Android (Google Play) e o instalador do app desktop do canal
    // deste ambiente. A trava de verdade é o `requireInterno` da rota.
    ...(user?.role === 'admin' ? [{
      id: 'apps',
      title: 'Apps',
      icon: '\u2B07\uFE0F',
      path: '/apps'
    }] : []),
    // Cenas SDUI (armazém por hash) — somente para role = admin
    ...(user?.role === 'admin' ? [{
      id: 'cenas',
      title: 'Cenas',
      icon: '🎬',
      path: '/cenas'
    }] : []),
    // Custos de Atendimento — o card saiu da Home e virou tela propria.
    ...(user?.role === 'admin' ? [{
      id: 'custos-atendimento',
      title: 'Custos de Atendimento',
      icon: '\u{1F4B0}',
      path: '/custos-atendimento'
    }] : []),
    // ===== MCP: o segundo item com submenu =====
    // Duas telas internas, agrupadas porque são a mesma coisa vista de dois ângulos: quem
    // PODE fazer, e o que foi FEITO. Separadas no menu, e não em abas, porque não
    // compartilham estado nenhum — ao contrário das abas de Configurações, que dividem o
    // ScopeSelector e perderiam o escopo a cada troca de rota.
    //
    // O `role === 'admin'` aqui só evita oferecer o que a API recusaria. A trava de verdade
    // é o `requireInterno` do routes/api.js, que exige e-mail @alia.do: role='admin' inclui
    // contas de CLIENTE, e estas telas mostram SQL rodado contra produção.
    // Regras e Conquistas — motor de regras (conquistas e push). A trava de verdade é o
    // `requireInterno` das rotas do BFF.
    ...(user?.role === 'admin' ? [{
      id: 'regras',
      title: 'Regras e Conquistas',
      icon: '\u{1F3C6}',
      path: '/regras',
    }] : []),
    // WhatsApp — catálogo e envios. Só leitura: cadastro e agendamento vivem no MCP.
    ...(user?.role === 'admin' ? [{
      id: 'whatsapp-relatorios',
      title: 'WhatsApp Relatórios',
      icon: '\u{1F4AC}',
      path: '/whatsapp/relatorios',
    }] : []),
    ...(user?.role === 'admin' ? [{
      id: 'mcp',
      title: 'MCP',
      icon: '\u{1F9E9}',
      children: [
        { id: 'mcp-permissoes', title: 'Permissões', icon: '\u{1F510}', path: '/mcp/permissoes' },
        { id: 'mcp-auditoria', title: 'Logs de Auditoria', icon: '\u{1F4DC}', path: '/mcp/auditoria' },
      ]
    }] : []),
    // AWON Monitor — por ultimo no menu, a pedido: uso pontual de diagnostico, nao rotina.
    ...(user?.role === 'admin' ? [{
      id: 'awon-monitor',
      title: 'AWON Monitor',
      icon: '\u{1F399}\uFE0F',
      path: '/awon-monitor'
    }] : [])
  ];

  // Menu padrão para usuários normais
  //
  // O 'Dash Operacional' (pages/Home.js) SAIU daqui em 02/09/2026, junto com a rota `/`
  // que o servia: o painel do cliente abre no Dash Analítico e o Monitor não é mais
  // oferecido em nenhum subdomínio de cliente. `admin.alia.do` usa o adminMenuItems e
  // não foi tocado.
  const defaultMenuItems = [
    // Primeiro item de propósito: é para onde a raiz do portal manda depois do login.
    // Sem trava de papel — quem não tem permissão de dashboard recebe a mesma tela de
    // "Nenhum dashboard disponível" que o AnalyticDashboardIndex mostra, e essa é a
    // única porta de volta para a home dele.
    {
      id: 'analytic-dashboard',
      title: 'Dashboard Analítico',
      icon: '📊',
      path: '/analytic-dashboard'
    },
    // 'Feedbacks' (/feedbacks-manager) saiu do menu em 02/09/2026: a tela demora demais
    // para carregar e não vai ser mexida agora. Só o item saiu — a ROTA continua de pé,
    // ao contrário do Dash Monitor, que perdeu as duas pontas. Quem tiver a URL ainda
    // abre a tela (lenta); o que se quis foi parar de OFERECER.
    // 'Agente Dashboard' escondido do menu (a pedido) — rota/feature mantidas.
    {
      id: 'settings',
      title: 'Configurações',
      icon: '⚙️',
      path: '/settings',
      isComingSoon: true
    }
  ];

  // Determinar quais itens de menu usar
  const menuItems = subdomain === 'admin' ? adminMenuItems : defaultMenuItems;

  // Grupos abertos. Um grupo começa ABERTO quando a página atual é de um filho dele: sem isso o
  // CS entra numa tela do grupo e o menu aparece fechado, como se ele estivesse em outro lugar.
  const [gruposAbertos, setGruposAbertos] = useState(() => {
    const inicial = {};
    (subdomain === 'admin' ? adminMenuItems : defaultMenuItems).forEach((it) => {
      if (it.children && it.children.some((c) => currentPage === c.path)) inicial[it.id] = true;
    });
    return inicial;
  });

  const handleItemClick = (item) => {
    if (item.isComingSoon) {
      return;
    }

    onNavigate(item.path);
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="sidebar-overlay"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${isOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <h3>{subdomain === 'admin' ? 'Admin' : 'Menu'}</h3>
          <button
            className="sidebar-close"
            onClick={onClose}
            aria-label="Fechar menu"
          >
            ✕
          </button>
        </div>

        <nav className="sidebar-nav">
          <ul className="sidebar-menu">
            {menuItems.map((item) => {
              // ---- item com submenu (um nível, de propósito) ----
              if (item.children) {
                const aberto = !!gruposAbertos[item.id];
                const filhoAtivo = item.children.some((c) => currentPage === c.path);
                return (
                  <li key={item.id} className="sidebar-menu-item">
                    <button
                      className={`sidebar-link sidebar-link-group ${filhoAtivo ? 'sidebar-link-parent-active' : ''}`}
                      onClick={() => setGruposAbertos((g) => ({ ...g, [item.id]: !g[item.id] }))}
                      aria-expanded={aberto}
                    >
                      <span className="sidebar-icon">{item.icon}</span>
                      <span className="sidebar-text">{item.title}</span>
                      <span className="sidebar-caret">{aberto ? '▾' : '▸'}</span>
                    </button>
                    {aberto && (
                      <ul className="sidebar-submenu">
                        {item.children.map((filho) => (
                          <li key={filho.id} className="sidebar-menu-item">
                            <button
                              className={`sidebar-link sidebar-link-child ${currentPage === filho.path ? 'sidebar-link-active' : ''}`}
                              onClick={() => handleItemClick(filho)}
                            >
                              <span className="sidebar-icon">{filho.icon}</span>
                              <span className="sidebar-text">{filho.title}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              }
              // ---- item simples (como sempre) ----
              return (
                <li key={item.id} className="sidebar-menu-item">
                  <button
                    className={`sidebar-link ${currentPage === item.path ? 'sidebar-link-active' : ''
                      } ${item.isComingSoon ? 'sidebar-link-disabled' : ''}`}
                    onClick={() => handleItemClick(item)}
                    disabled={item.isComingSoon}
                  >
                    <span className="sidebar-icon">{item.icon}</span>
                    <span className="sidebar-text">{item.title}</span>
                    {item.isComingSoon && (
                      <span className="sidebar-badge">Em breve</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-version">
            <small>Aliado Admin v{process.env.REACT_APP_VERSION || '1.0.0'}</small>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;